import { useEffect, useState } from 'react';
import { AUTO, FIELD, HYBRID, OFFICE, modeForWidth, storedOverride, setOverride } from '../utils/layoutMode';

// ── THE LAYOUT MODE, LIVE ───────────────────────────────────────────────────
// Reactive to resize, rotate and FOLD. A Fold goes 348px -> 690px mid-task, on
// the same phone, and the chrome has to follow it.
//
// ⚠ A RESIZE LISTENER, NOT matchMedia, and on purpose — unlike useDevice.js.
// This hook needs the WIDTH itself (for the override to be overridable and for
// screens to reason about 348 vs 430), not just which of three buckets it
// falls in. rAF-coalesced so a drag resizes once per frame, not per pixel.
export default function useLayoutMode() {
  const read = () => {
    const width = typeof window === 'undefined' ? 0 : window.innerWidth;
    const override = storedOverride();
    return { width, override, mode: override === AUTO ? modeForWidth(width) : override };
  };
  const [state, setState] = useState(read);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let frame = 0;
    const onResize = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => { frame = 0; setState(read()); });
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    // ⚠ The override is per device and changed in Settings, which may be a
    // different tab or a different route in the same tab. `storage` covers
    // the first; the custom event covers the second, because `storage` does
    // NOT fire in the tab that made the change.
    const onStore = () => setState(read());
    window.addEventListener('storage', onStore);
    window.addEventListener('roofbeam:layoutmode', onStore);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      window.removeEventListener('storage', onStore);
      window.removeEventListener('roofbeam:layoutmode', onStore);
    };
  }, []);

  return state;   // { mode, width, override }
}

// Setting it from anywhere notifies every hook in this tab.
export function applyLayoutMode(mode) {
  const ok = setOverride(mode);
  try { window.dispatchEvent(new Event('roofbeam:layoutmode')); } catch (_) { /* older browser */ }
  return ok;
}

export { AUTO, FIELD, HYBRID, OFFICE };
